/**
 * PieChartEstatus — pie chart counts por estatus.
 *
 * Per FWA-PWA-032 G0 D5. Recharts declarative + colored cells per estatus.
 */
import type { PorEstatusCounts } from '@sisrep/schemas';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

const COLORS_BY_ESTATUS: Record<string, string> = {
  activa: '#16a34a', // green-600
  baja: '#dc2626', // red-600
  pausa: '#d97706', // amber-600
  egresada: '#64748b', // slate-500
};

const ESTATUS_LABELS: Record<string, string> = {
  activa: 'Activa',
  baja: 'Baja',
  pausa: 'Pausa',
  egresada: 'Egresada',
};

interface PieChartEstatusProps {
  counts: PorEstatusCounts;
}

export function PieChartEstatus({ counts }: PieChartEstatusProps) {
  const data = Object.entries(counts)
    .map(([estatus, count]) => ({
      estatus: ESTATUS_LABELS[estatus] ?? estatus,
      estatusKey: estatus,
      count,
    }))
    .filter((d) => d.count > 0);

  if (data.length === 0) {
    return <p className="text-center py-8 text-gray-500">Sin datos por estatus.</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <PieChart>
        <Pie
          data={data}
          dataKey="count"
          nameKey="estatus"
          outerRadius={100}
          label={(entry: { name?: string; percent?: number }) =>
            `${entry.name ?? ''} ${((entry.percent ?? 0) * 100).toFixed(0)}%`
          }
        >
          {data.map((entry) => (
            <Cell key={entry.estatusKey} fill={COLORS_BY_ESTATUS[entry.estatusKey] ?? '#94a3b8'} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  );
}
